import type { Constructor, Container } from '@packages/core/container';

import { type CaseStatusUpdateBody, StsBackendClient } from '@packages/clients';
import { ANALYTICS_EVENTS, AnalyticsService } from '@packages/core/analytics';

import { StsBackendOperation } from '../../../sts-backend-operation';
import { buildCloseCaseBody } from '../helpers';

/** Parameters for closing a techline case. */
export type CloseCaseParams = {
    /** The case number to close. */
    caseNumber: string;

    /** The closure description explaining why the case is being closed. */
    description: string;

    /** Whether the vehicle was repaired before closing. */
    isVehicleRepaired: boolean;

    /** The retailer number associated with the case. */
    retailerNumber: string | undefined;

    /** The username making the closure. */
    username: string;
};

/**
 * Close a techline case by transitioning its status to CLOSED.
 * Builds the closure payload with a formatted date and logs an
 * analytics event on success.
 */
export class CloseCase extends StsBackendOperation {
    /**
     * Register this operation with additional dependency on AnalyticsService.
     *
     * @param container - the container to register in
     */
    static override registerInContainer(container: Container): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- static `this` in an abstract class is not assignable to Constructor without a cast
        container.register(this as any as Constructor, [StsBackendClient, AnalyticsService]);
    }

    /** @param analyticsService - the analytics service for logging events */
    private analyticsService: AnalyticsService;

    /**
     * @param client - the backend API client
     * @param analyticsService - the analytics service for logging events
     */
    constructor(client: StsBackendClient, analyticsService: AnalyticsService) {
        super(client);
        this.analyticsService = analyticsService;
    }

    /**
     * Build the closure payload and update the case status to CLOSED.
     *
     * @param params - the closure details and identity fields
     * @returns true if the closure succeeded
     */
    async exec(params: CloseCaseParams): Promise<boolean> {
        const updateBody = buildCloseCaseBody({
            ...params,
            closureDate: new Date(),
        }) as CaseStatusUpdateBody;

        const response = await this.client.updateCaseStatus({ updateBody });

        if (response?.success) {
            this.analyticsService.logEvent(ANALYTICS_EVENTS.USER_SUCCESSFULLY_CLOSES_TECHLINE_CASE);
            return true;
        }

        return false;
    }
}
